import {
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { DeleteIcon, PencilIcon } from '@atgfr8/icons';
import { itemsClass } from '@common/commonLists';
import Notification from '@components/Notification/Notification';
import Popover from '@components/Popover/Popover';
import { useIndexedPopoverState } from '@components/Popover/usePopoverRef';
import { SelectInput } from '@components/Select/SelectInput';
import { LTLHazmatDetails } from '@store/ltl/types';
import { HazmatDetails, LoadItemBreakdown } from '@store/orders/types';
import { initialHazmatDetails } from '@store/orders/defaultVals';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { BreakdownDeviation } from '@utils/ltl/loadItemBreakdown';
import { Index, Show } from 'solid-js';

import cls from './classes';
import { HazardousMaterial } from './HazardousMaterial';

// Per-row validation errors for a sub-item, as felte shapes them from the
// nested loadItemBreakdowns schema in the stop validations.
export type BreakdownValidationErrors = {
  weight?: string[] | string | null;
  class?: string[] | string | null;
};

type StopItemBreakdownProps = {
  itemIndex: number;
  breakdowns: LoadItemBreakdown[];
  hidden: boolean;
  columnsLength: number;
  deviations: BreakdownDeviation[];
  errors?: (BreakdownValidationErrors | null)[] | null;
  onChange: (subIndex: number, patch: Partial<LoadItemBreakdown>) => void;
  onDelete: (subIndex: number) => void;
};

const hasHazmatValues = (detail: HazmatDetails | null | undefined) =>
  [
    detail?.unNumber,
    detail?.packingGroup,
    detail?.hazardClass,
    detail?.piece,
    detail?.contactNumber,
    detail?.contactName,
  ].some((value) => Boolean(value?.trim()));

// A detail is persisted unless it was created in this editing session. Saved
// rows come back from the order API without an operationType.
const isUnsavedHazmatDetail = (detail: HazmatDetails | null | undefined) =>
  !detail || detail.operationType === 'Insert';

// Turning hazmat off: drop an unsaved detail, soft-delete a persisted one so
// the API removes it, mirroring the parent-item handler in StopItems.
const clearedHazmatDetail = (
  detail: HazmatDetails | null | undefined,
): HazmatDetails | null =>
  isUnsavedHazmatDetail(detail)
    ? null
    : { ...(detail as HazmatDetails), operationType: 'Delete' };

// Turning hazmat back on: revive a soft-deleted detail as an update instead
// of inserting a duplicate; otherwise keep what is there or start fresh.
const restoredHazmatDetail = (
  detail: HazmatDetails | null | undefined,
): HazmatDetails => {
  if (!detail) return initialHazmatDetails;
  if (detail.operationType === 'Delete') {
    return { ...detail, operationType: 'Update' };
  }
  return detail;
};

// Saving the popover: keep the persisted identity and flag it as an update.
const savedHazmatDetail = (
  existing: HazmatDetails | null | undefined,
  saved: LTLHazmatDetails,
): HazmatDetails => ({
  ...initialHazmatDetails,
  ...existing,
  ...saved,
  operationType: isUnsavedHazmatDetail(existing) ? 'Insert' : 'Update',
});

const errorMessages = (
  value: string[] | string | null | undefined,
): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const formatDeviation = (deviation: BreakdownDeviation) =>
  `Sub-item ${deviation.subIndex + 1} ${deviation.field} changed from ${deviation.original} to ${deviation.current}. A requote may be required.`;

// Mixed-commodity sub-item rows rendered under an LTL load item in StopItems.
// Column positions line up with the LTL layout of the parent table.
export const StopItemBreakdown = (props: StopItemBreakdownProps) => {
  const hazmatPopover = useIndexedPopoverState(
    (subIndex) =>
      `[data-breakdown-hazmat-key="${props.itemIndex}-${subIndex}"]`,
  );

  const rowErrors = (subIndex: number) => props.errors?.[subIndex] ?? null;

  // Flat list of "Sub-item N: message" lines for the notification under the rows.
  const subItemErrorLines = () =>
    (props.errors ?? []).flatMap((rowError, subIndex) =>
      [
        ...errorMessages(rowError?.weight),
        ...errorMessages(rowError?.class),
      ].map((message) => `Sub-item ${subIndex + 1}: ${message}`),
    );

  const handleHazmatToggle = (
    breakdown: LoadItemBreakdown,
    subIndex: number,
    checked: boolean,
  ) => {
    if (checked) {
      props.onChange(subIndex, {
        hazmat: true,
        loadItemHazmatDetail: restoredHazmatDetail(
          breakdown.loadItemHazmatDetail,
        ),
      });
      hazmatPopover.open(subIndex);
      return;
    }
    props.onChange(subIndex, {
      hazmat: false,
      loadItemHazmatDetail: clearedHazmatDetail(breakdown.loadItemHazmatDetail),
    });
    hazmatPopover.close();
  };

  const handleHazmatClose = (
    breakdown: LoadItemBreakdown,
    subIndex: number,
  ) => {
    if (!hasHazmatValues(breakdown.loadItemHazmatDetail)) {
      props.onChange(subIndex, {
        hazmat: false,
        loadItemHazmatDetail: clearedHazmatDetail(
          breakdown.loadItemHazmatDetail,
        ),
      });
    }
    hazmatPopover.close();
  };

  return (
    <>
      <Index each={props.breakdowns}>
        {(breakdown, subIndex) => (
          <TableRow
            style={{
              'vertical-align': 'middle',
              display:
                props.hidden || breakdown().operationType === 'Delete'
                  ? 'none'
                  : 'table-row',
            }}
          >
            <TableCell />
            <TableCell />
            <TableCell>
              <TextField
                label=""
                class={cls.itemFieldInput}
                size="sm"
                placeholder="Description"
                style={{ width: '100%' }}
                value={breakdown().description}
                maxLength={150}
                onChange={(value: string) =>
                  props.onChange(subIndex, { description: value })
                }
                dataTestId={`breakdown-description-${props.itemIndex}-${subIndex}`}
              />
            </TableCell>
            <TableCell>
              <TextField
                type="number"
                label=""
                class={cls.itemFieldInput}
                size="sm"
                placeholder="#"
                style={{ width: '100%' }}
                value={breakdown().pieces ?? ''}
                maxLength={7}
                onChange={(value: string) =>
                  props.onChange(subIndex, {
                    pieces: value === '' ? null : value,
                  })
                }
                dataTestId={`breakdown-pieces-${props.itemIndex}-${subIndex}`}
              />
            </TableCell>
            <TableCell>
              <TextField
                type="number"
                label=""
                class={cls.itemFieldInput}
                size="sm"
                placeholder="Weight"
                style={{ width: '100%' }}
                value={breakdown().weight ?? ''}
                maxLength={6}
                error={Boolean(rowErrors(subIndex)?.weight)}
                onChange={(value: string) =>
                  props.onChange(subIndex, {
                    weight: value === '' ? null : Number(value),
                  })
                }
                dataTestId={`breakdown-weight-${props.itemIndex}-${subIndex}`}
              />
            </TableCell>
            <TableCell />
            <TableCell>
              <TextField
                label=""
                class={cls.itemFieldInput}
                size="sm"
                placeholder="NMFC"
                style={{ width: '100%' }}
                value={breakdown().nmfc ?? ''}
                maxLength={10}
                onChange={(value: string) =>
                  props.onChange(subIndex, { nmfc: value })
                }
                dataTestId={`breakdown-nmfc-${props.itemIndex}-${subIndex}`}
              />
            </TableCell>
            <TableCell>
              <SelectInput
                label=""
                class="!h-[36px]"
                value={breakdown().class?.toString() ?? ''}
                error={errorMessages(rowErrors(subIndex)?.class)[0] ?? null}
                menuItems={itemsClass.map((c) => ({ label: c, value: c }))}
                onChange={(value) =>
                  props.onChange(subIndex, { class: String(value) })
                }
                dataTestId={formatDataTestId(
                  'Stop item breakdown freight class',
                  'selectInput',
                )}
              />
            </TableCell>
            <TableCell>
              <div
                class="flex items-center"
                data-breakdown-hazmat-key={`${props.itemIndex}-${subIndex}`}
              >
                <Checkbox
                  checked={Boolean(breakdown().hazmat)}
                  onChange={(checked: boolean) =>
                    handleHazmatToggle(breakdown(), subIndex, checked)
                  }
                  on:mousedown={(e) => e.stopPropagation()}
                  dataTestId={`breakdown-hazmat-checkboxInput-${props.itemIndex}-${subIndex}`}
                  size="xs"
                  color="info"
                  class="ml-[-9px] mr-[4px]"
                />
                <Popover
                  forceClosePopover={!breakdown().hazmat}
                  triggerClose={hazmatPopover.triggerClose(subIndex)}
                  onClick={hazmatPopover.onTriggerClick(subIndex)}
                  eleName={
                    <PencilIcon
                      color={Boolean(breakdown().hazmat) ? 'black' : 'gray'}
                    />
                  }
                  preventFormSubmit
                  classes={cls.reminderTxt}
                  sxProps={{
                    'background-color': 'white',
                    'border-radius': '0',
                    'text-decoration': 'underline !important',
                  }}
                  class="z-[9999]!"
                  onOutsideClose={() =>
                    handleHazmatClose(breakdown(), subIndex)
                  }
                >
                  <HazardousMaterial
                    fromSidebar={false}
                    loadItemHazmatDetail={
                      breakdown().loadItemHazmatDetail ?? initialHazmatDetails
                    }
                    onSave={(hazmatDetail: LTLHazmatDetails) => {
                      props.onChange(subIndex, {
                        hazmat: true,
                        loadItemHazmatDetail: savedHazmatDetail(
                          breakdown().loadItemHazmatDetail,
                          hazmatDetail,
                        ),
                      });
                      hazmatPopover.close();
                    }}
                    onClose={() => handleHazmatClose(breakdown(), subIndex)}
                  />
                </Popover>
              </div>
            </TableCell>
            <TableCell />
            <TableCell />
            <TableCell>
              <div class="flex mt-[10px] items-center">
                <IconButton
                  title="Delete Sub-item"
                  dataTestId={`delete-stop-item-breakdown-${props.itemIndex}-${subIndex}`}
                  on:click={(e) => {
                    e.preventDefault();
                    props.onDelete(subIndex);
                  }}
                >
                  <DeleteIcon color="#B00020" />
                </IconButton>
              </div>
            </TableCell>
          </TableRow>
        )}
      </Index>
      <Show when={!props.hidden && subItemErrorLines().length > 0}>
        <TableRow>
          <td colSpan={props.columnsLength}>
            <Notification
              type="error"
              text={
                <ul class="list-disc my-[8px] pl-[24px]">
                  <Index each={subItemErrorLines()}>
                    {(line) => <li>{line()}</li>}
                  </Index>
                </ul>
              }
            />
          </td>
        </TableRow>
      </Show>
      <Show when={!props.hidden && props.deviations.length > 0}>
        <TableRow>
          <td colSpan={props.columnsLength}>
            <Notification
              type="warning"
              text={
                <ul class="list-disc my-[8px] pl-[24px]">
                  <Index each={props.deviations}>
                    {(deviation) => <li>{formatDeviation(deviation())}</li>}
                  </Index>
                </ul>
              }
            />
          </td>
        </TableRow>
      </Show>
    </>
  );
};
